/**
 * Per-board "things for you" strip — pending tasks (mention or nudge)
 * for the current user on this board. Hides itself when empty.
 */

import { useEffect, useState } from 'react';
import { api, type TaskRow } from '../api';
import { useRouter } from '../router';
import { IconCheck, IconChevronRight, IconSparkles } from './Icon';
import { RenderNodes } from './CommentRender';
import { UiNode } from '../lib/UiNode';
import { formatDueDate } from '../../shared/comment-body';
import { keyOf } from './TasksList';

export function MyTasksStrip({ boardId }: { boardId: string }) {
  const [tasks, setTasks] = useState<TaskRow[] | null>(null);
  const { navigate } = useRouter();

  useEffect(() => {
    let cancelled = false;
    const load = () => {
      void api
        .myTasks(boardId)
        .then((r) => {
          if (!cancelled) setTasks(r.tasks);
        })
        .catch(() => undefined);
    };
    load();
    // Refresh when the window regains focus OR an in-app event fires.
    // The intent panel dispatches 'synfolio:tasks-stale' after writing,
    // so the nudge disappears immediately instead of after a reload.
    const onFocus = () => load();
    const onStale = () => load();
    window.addEventListener('focus', onFocus);
    window.addEventListener('synfolio:tasks-stale', onStale);
    return () => {
      cancelled = true;
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('synfolio:tasks-stale', onStale);
    };
  }, [boardId]);

  if (!tasks || tasks.length === 0) return null;

  async function markMentionDone(t: TaskRow & { kind: 'mention' }) {
    setTasks((cur) => (cur ?? []).filter((x) => keyOf(x) !== keyOf(t)));
    try {
      await api.completeTask(t.commentId);
    } catch {
      const r = await api.myTasks(boardId);
      setTasks(r.tasks);
    }
  }

  return (
    <UiNode
      as="section"
      name="board-tasks-strip"
      entity="tasks"
      state={tasks.length === 0 ? 'empty' : 'filled'}
      className="panel p-3"
    >
      <header className="mb-2 flex items-baseline justify-between">
        <h3 className="text-xs uppercase tracking-wide text-cream-3">
          For you · {tasks.length}
        </h3>
        <button
          type="button"
          className="text-xs text-cream-3 hover:text-cream-2"
          onClick={() => navigate('/')}
        >
          All tasks <IconChevronRight size={10} />
        </button>
      </header>
      <ul className="flex gap-2 overflow-x-auto pb-1">
        {tasks.slice(0, 6).map((t) => (
          <li
            key={keyOf(t)}
            className="panel-elev flex min-w-[260px] max-w-[320px] flex-shrink-0 flex-col gap-2 p-2.5"
          >
            <div className="flex items-baseline justify-between gap-2 text-[11px] text-cream-3">
              <span className="truncate">
                {t.kind === 'mention' ? (
                  <>
                    <span className="font-medium text-cream-2">{t.authorName}</span> ·{' '}
                    {t.subjectLabel}
                  </>
                ) : (
                  <span className="inline-flex items-center gap-1 uppercase tracking-wide">
                    <IconSparkles size={10} />
                    Alignment
                  </span>
                )}
              </span>
              {t.dueIso && (
                <span className="chip whitespace-nowrap" title={t.dueIso}>
                  {formatDueDate(t.dueIso)}
                </span>
              )}
            </div>
            <button
              type="button"
              onClick={() => navigate(t.href)}
              className="line-clamp-2 break-words text-left text-sm hover:underline"
            >
              <RenderNodes nodes={t.bodyNodes} />
            </button>
            <div className="mt-auto flex items-center gap-1.5">
              {t.kind === 'mention' ? (
                <>
                  <button
                    type="button"
                    className="btn btn-ghost inline-flex items-center gap-1 text-[11px]"
                    onClick={() => markMentionDone(t)}
                  >
                    <IconCheck size={11} />
                    Done
                  </button>
                  <button
                    type="button"
                    className="btn btn-ghost ml-auto text-[11px]"
                    onClick={() => navigate(t.href)}
                  >
                    Open
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary ml-auto text-[11px]"
                  onClick={() => navigate(t.href)}
                >
                  Write
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>
    </UiNode>
  );
}
